import debounce from "lodash/debounce";
import { jsx } from "panel";
import { ButtonEl } from "common/components/button";
import { ButtonSize, ButtonTheme } from "common/components/button/constants";
import { CalendarEl } from "common/components/calendar";
import { InputEl } from "common/components/input";
import { ProgressBarEl } from "common/components/progress-bar";
import { SelectEl } from "common/components/select";
import { SvgIconEl } from "common/components/svg-icon";
import { TileEl, TileTheme } from "common/components/tile";
import { QueryEntrySectionEl } from "common/query-builder/query-entry-section/panel";
import {
  RowLabelType,
  SelectorTypeDefinitions
} from "common/query-builder/query-entry-section/types";
import { TemplateType } from "common/report/dashboards/types";
import { SpinnerEl } from "common/components/spinner";
import { SpinnerColor } from "common/components/spinner/types";
import { TooltipEl } from "common/components/tooltip";
import { Placement } from "common/components/tooltip/types";
import { isBoardsToReportsEnabled } from "common/report/util";
import { EventAndListPropertySelector } from "./components/EventAndListPropertySelector";
import { FieldLabel } from "./components/FieldLabel";
import {
  BenchmarkKPITemplateFields,
  ECommerceTemplateFields,
  KPITemplateFields,
  LEARN_MORE_LINK_MAP,
  LifecycleCohortAnalysisTemplateFields,
  MarketingKPITemplateFields,
  ProductLaunchTemplateFields,
  RHSInputType,
  RHSPanelMode,
  WebAnalyticsTemplateFields
} from "./types";
import calendarButtonOverride from "./calendar-button-override.inline.styl";
const commonParams = {
  buttonTheme: ButtonTheme.QueryBuilder,
  disableAddEventFilters: true,
  disableCloneEntry: true,
  disableDeleteEntry: false,
  disableFirstTimeEventFilters: true,
  rowLimit: 1,
  rowLabelType: RowLabelType.None
};
export const PAGE_LOADED_TOOLTIP = `This is how Page Views are tracked on your website.`;
export const PAGE_NAME_TOOLTIP = `This is how Page Names are tracked on your website.`;
export function template() {
  const preselectedEventsSectionFactory = (eventName, tooltipText = null, infoText = null, showReminderTooltip) => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".event-mapping-row.short-field"
    }, FieldLabel(eventName, tooltipText), infoText && /* @__PURE__ */ jsx("div", {
      sel: ".info-text"
    }, infoText), /* @__PURE__ */ jsx(QueryEntrySectionEl, {
      ...commonParams,
      addEntryButtonLabel: "Add Event",
      projectId: parseInt(this.getContext(`currentProject`).id, 10),
      trackName: "RHS",
      onUpdate: (updates) => this.helpers.handleEventUpdate(updates, eventName),
      sel: ".query-entry-section"
    }), showReminderTooltip && /* @__PURE__ */ jsx(TooltipEl, {
      attrs: {
        placement: Placement.Left,
        offset: 28,
        skidding: infoText ? 58 : 40,
        "always-show": true,
        "delay-in": 10,
        "delay-out": 10,
        "max-width": `200px`
      }
    }, "Enter in your events on the set up panel and click Save in order to view these reports."));
  };
  const propertiesSelector = (propertyKey, tooltipText, infoText) => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".event-mapping-row.short-field"
    }, FieldLabel(propertyKey, tooltipText), infoText && /* @__PURE__ */ jsx("div", {
      sel: ".info-text"
    }, infoText), /* @__PURE__ */ jsx(QueryEntrySectionEl, {
      ...commonParams,
      disableAddEntryButtonTooltipText: true,
      disableMapToLookupTable: true,
      disableTypecast: true,
      addEntryButtonLabel: "Add Property",
      projectId: parseInt(this.getContext(`currentProject`).id, 10),
      rowTypes: [SelectorTypeDefinitions.Segment],
      onUpdate: (updates) => {
        if (updates.entries.length) {
          this.helpers.updateField(
            updates.updatedEntry.event.propertyName,
            propertyKey,
            RHSInputType.Property
          );
        } else {
          this.helpers.removeField(propertyKey);
        }
      }
    }));
  };
  const templateCalendarInputField = (fieldName) => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".event-mapping-row.short-field"
    }, FieldLabel(fieldName), /* @__PURE__ */ jsx(ButtonEl, {
      attrs: {
        icon: `calendar`,
        theme: ButtonTheme.TemplatesEvent,
        pressed: this.state.calendarInputOpen,
        elref: `calendarButton`,
        "style-override": calendarButtonOverride
      },
      on: { click: () => this.update({ calendarInputOpen: true }) }
    }, this.state.fields[fieldName] || `Select date`), this.state.calendarInputOpen && /* @__PURE__ */ jsx("div", null, /* @__PURE__ */ jsx(CalendarEl, {
      attrs: {
        "is-double-calendar": false,
        "is-range": false,
        "is-from-date-focused": true
      },
      on: {
        selectDate: (ev) => {
          this.helpers.updateField(ev.detail.date, fieldName, RHSInputType.Date);
          this.update({ calendarInputOpen: false });
        }
      }
    })));
  };
  const debouncedHandleInput = debounce((value, fieldName) => {
    this.helpers.updateField(`\u2018${value}\u2019`, fieldName, RHSInputType.Text);
  }, 500);
  const templateInputField = (fieldName, placeholder) => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".event-mapping-row"
    }, FieldLabel(fieldName), /* @__PURE__ */ jsx(InputEl, {
      attrs: {
        placeholder,
        "style-override": `.mp-input-container input.mp-input {background-color: var(--background-color)}`
      },
      on: {
        input: (ev) => debouncedHandleInput(ev.target.value, fieldName)
      }
    }));
  };
  const kpiTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, preselectedEventsSectionFactory(
      KPITemplateFields.SIGN_UP,
      `This is the event where a new user\u2014free or paid\u2014is acquired by creating a new account. Your event might be called \u201CAccount created\u201D instead of \u201CSign up.\u201D Don't have this type of event? Try using \u201CSession start\u201D or a similar event. [Learn More](${this.WHAT_TO_TRACK_URL})`,
      null,
      this.helpers.shouldShowReminderTooltip()
    ), preselectedEventsSectionFactory(KPITemplateFields.VALUE_MOMENT, this.VALUE_MOMENT_FIELD_TOOLTIP_TEXT));
  };
  const productLaunchTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, templateInputField(ProductLaunchTemplateFields.FEATURE_NAME, `Ex. Reactions on Comments`), templateCalendarInputField(`Launch Date`), preselectedEventsSectionFactory(
      ProductLaunchTemplateFields.NEW_FEATURE,
      `This is the event that captures the adoption of your new feature. For adding reviews to a streaming service, the event could be publishing a review.`,
      null,
      this.helpers.shouldShowReminderTooltip()
    ), preselectedEventsSectionFactory(
      ProductLaunchTemplateFields.FEATURE_VALUE_MOMENT,
      this.VALUE_MOMENT_FIELD_TOOLTIP_TEXT
    ));
  };
  const lifecycleCohortAnalysisTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, preselectedEventsSectionFactory(
      LifecycleCohortAnalysisTemplateFields.VALUE_MOMENT,
      this.VALUE_MOMENT_FIELD_TOOLTIP_TEXT,
      null,
      this.helpers.shouldShowReminderTooltip()
    ));
  };
  const marketingKpiTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, preselectedEventsSectionFactory(
      MarketingKPITemplateFields.ACTIVATION_EVENT,
      `This is the event that marks a user as "active" for you. This could be viewing a webpage or entering an email address, or opening an app.`,
      `Will default to "Any Event" if none selected`,
      this.helpers.shouldShowReminderTooltip()
    ), preselectedEventsSectionFactory(
      MarketingKPITemplateFields.CONVERSION_EVENT,
      `This is the desired user behavior. For a social media platform, this could be creating a post, for e-commerce it might be completing a purchase.`
    ));
  };
  const eCommerceTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, EventAndListPropertySelector({
      cmp: this,
      eventName: ECommerceTemplateFields.PURCHASE_EVENT,
      propertyName: ECommerceTemplateFields.CART_PROPERTY,
      propertyKey: ECommerceTemplateFields.PRICE_KEY,
      label: `Purchase, Cart, and Price`,
      disabled: false,
      tooltipText: `Adding a purchase event, cart property, and price property helps us uncover user purchase behaviors.

          In order to use this template we require a cart property with your purchase event. If you don't have one set up, learn how to set it up [here](${this.CART_ANALYSIS_URL}).`,
      event: `Purchase`,
      propertyNameLabel: `Cart Property`,
      propertyKeyLabel: `Price Property`,
      showReminderTooltip: this.helpers.shouldShowReminderTooltip()
    }), EventAndListPropertySelector({
      cmp: this,
      eventName: ECommerceTemplateFields.ADD_TO_CART_EVENT,
      propertyName: ECommerceTemplateFields.CART_PROPERTY_2,
      propertyKey: ECommerceTemplateFields.CATEGORY_KEY,
      label: `Add to Cart, Cart, and Category`,
      disabled: false,
      tooltipText: `Adding an Add to Cart event, cart property, and category property helps us uncover user purchase behaviors.

          In order to use this template we require a cart property with your Add to Cart event. If you don't have one set up, learn how to set it up [here](${this.CART_ANALYSIS_URL}).`,
      event: `Add to Cart`,
      propertyNameLabel: `Cart Property`,
      propertyKeyLabel: `Category Property`
    }), preselectedEventsSectionFactory(
      ECommerceTemplateFields.PRODUCT_VIEWED_EVENT,
      `The Product Viewed Event helps us see users behavior across the entire purchasing funnel. [Learn More](${this.WHAT_TO_TRACK_URL})`
    ));
  };
  const webAnalyticsTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, preselectedEventsSectionFactory(
      WebAnalyticsTemplateFields.PAGE_LOADED_EVENT,
      PAGE_LOADED_TOOLTIP,
      `What do you consider a page view?`,
      this.helpers.shouldShowReminderTooltip()
    ), propertiesSelector(
      WebAnalyticsTemplateFields.PAGE_NAME_PROPERTY,
      PAGE_NAME_TOOLTIP,
      `How do you identify the name of a page?`
    ));
  };
  const benchmarkIndustrySelector = () => {
    var _a;
    return /* @__PURE__ */ jsx("div", {
      sel: ".event-mapping-row.short-field"
    }, FieldLabel(`Industry`, null), /* @__PURE__ */ jsx(SelectEl, {
      sel: ".benchmark-industry-selector",
      attrs: {
        "menu-height": 500,
        "selected-option": (_a = this.state.fields[BenchmarkKPITemplateFields.INDUSTRY]) != null ? _a : `All`,
        sections: [
          {
            items: [
              `All`,
              `Technology`,
              `Media & Entertainment`,
              `Financial Services`,
              `Retailing`,
              `Healthcare`,
              `Gaming`
            ].map((item) => ({
              label: item,
              value: item
            }))
          }
        ]
      },
      on: {
        change: (ev) => {
          this.helpers.updateField(ev.detail.value, BenchmarkKPITemplateFields.INDUSTRY, RHSInputType.Text);
        }
      }
    }));
  };
  const benchmarkKpiTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, preselectedEventsSectionFactory(
      BenchmarkKPITemplateFields.SIGN_UP,
      `This is the event where a new user\u2014free or paid\u2014is acquired by creating a new account. Your event might be called \u201CAccount created\u201D instead of \u201CSign up.\u201D Don't have this type of event? Try using \u201CSession start\u201D or a similar event. [Learn More](${this.WHAT_TO_TRACK_URL})`,
      null,
      this.helpers.shouldShowReminderTooltip()
    ), preselectedEventsSectionFactory(BenchmarkKPITemplateFields.VALUE_MOMENT, this.VALUE_MOMENT_FIELD_TOOLTIP_TEXT), benchmarkIndustrySelector());
  };
  const templateFields = () => {
    switch (this.params.selectedTemplate) {
      case TemplateType.KPI:
        return kpiTemplateFields();
      case TemplateType.FEATURE_LAUNCH:
        return productLaunchTemplateFields();
      case TemplateType.LIFECYCLE_COHORT_ANALYSIS:
        return lifecycleCohortAnalysisTemplateFields();
      case TemplateType.MARKETING_KPI:
        return marketingKpiTemplateFields();
      case TemplateType.E_COMMERCE:
        return eCommerceTemplateFields();
      case TemplateType.WEB_ANALYTICS:
        return webAnalyticsTemplateFields();
      case TemplateType.BENCHMARK_KPI:
        return benchmarkKpiTemplateFields();
      default:
        return null;
    }
  };
  const progressBar = (length) => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".progress-bar-container"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".current-step"
    }, this.state.tutorialCurrentStepIndex + 1 === length ? `Completed` : `Step ${this.state.tutorialCurrentStepIndex + 1} of ${length - 1}`), /* @__PURE__ */ jsx(ProgressBarEl, {
      sel: ".progress-bar-meter",
      attrs: { percent: (this.state.tutorialCurrentStepIndex + 1) / length * 100 }
    }));
  };
  const eventsInfo = () => {
    const learnMoreLink = LEARN_MORE_LINK_MAP.get(this.params.selectedTemplate);
    let setupString;
    let link;
    if (this.params.selectedTemplate === TemplateType.E_COMMERCE) {
      setupString = `This template involves both events and properties to use. `;
    } else {
      setupString = `Add ${this.helpers.getInfoEventString()} to get started with this template. `;
    }
    if (this.params.selectedTemplate === TemplateType.E_COMMERCE) {
      link = /* @__PURE__ */ jsx("a", {
        attrs: { href: learnMoreLink, target: `_blank` }
      }, "Learn More");
    } else if (this.params.selectedTemplate === TemplateType.LIFECYCLE_COHORT_ANALYSIS) {
      link = /* @__PURE__ */ jsx("span", null, "Learn about your ", /* @__PURE__ */ jsx("a", {
        attrs: { href: this.VALUE_MOMENT_URL, target: `_blank` }
      }, "value moment"), ".");
    } else {
      link = /* @__PURE__ */ jsx("a", {
        attrs: { href: this.WHY_THESE_EVENTS_URL, target: `_blank` }
      }, "Why these events?");
    }
    return /* @__PURE__ */ jsx("div", {
      sel: ".events-info"
    }, setupString, link);
  };
  const tutorial = () => {
    const { showTutorial } = this.state;
    const tutorialConfig = this.helpers.getTemplateTutorialConfig();
    if (tutorialConfig && !this.state.hideTutorial) {
      return /* @__PURE__ */ jsx(TileEl, {
        sel: ".info-tile",
        params: {
          theme: TileTheme.NO_IMAGE,
          fillWidth: true
        }
      }, /* @__PURE__ */ jsx(null, null, showTutorial ? /* @__PURE__ */ jsx("div", null, Object.values(tutorialConfig)[this.state.tutorialCurrentStepIndex].stepInfo, progressBar(Object.values(tutorialConfig).length)) : /* @__PURE__ */ jsx(null, null, "Need help setting up this template? Try out a walkthrough.", /* @__PURE__ */ jsx("div", {
        sel: ".tutorial-button-container"
      }, /* @__PURE__ */ jsx(ButtonEl, {
        attrs: { size: ButtonSize.Small, theme: ButtonTheme.Primary },
        on: { click: this.helpers.startTutorial }
      }, "Show me how"), /* @__PURE__ */ jsx(ButtonEl, {
        attrs: { size: ButtonSize.Small },
        on: { click: () => this.helpers.dismissTutorial() }
      }, "Not now")))));
    }
    return null;
  };
  const eventSelectStep = () => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".event-select-step"
    }, eventsInfo(), tutorial(), templateFields());
  };
  return /* @__PURE__ */ jsx("div", {
    sel: ".right-panel",
    class: {
      "boards-to-reports": isBoardsToReportsEnabled(this.getContext(`currentProject`).get().value)
    }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".container",
    class: { templates: this.params.mode === RHSPanelMode.Templates }
  }, this.params.mode === RHSPanelMode.Templates && /* @__PURE__ */ jsx("div", {
    sel: ".templates-back-btn"
  }, this.params.isSeeAllTemplatesLoading ? /* @__PURE__ */ jsx(SpinnerEl, {
    attrs: { color: SpinnerColor.Inherit }
  }) : /* @__PURE__ */ jsx("div", {
    sel: ".click-area",
    on: { click: this.helpers.handleSeeAllTemplatesClick }
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    attrs: { icon: `arrow-left` }
  }), /* @__PURE__ */ jsx("span", null, "See all templates"))), /* @__PURE__ */ jsx("div", {
    sel: ".header",
    class: { templates: this.params.mode === RHSPanelMode.Templates }
  }, "Set Up Board"), /* @__PURE__ */ jsx("div", {
    sel: ".body",
    class: { templates: this.params.mode === RHSPanelMode.Templates }
  }, this.params.mode === RHSPanelMode.StarterBoard && /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".event-description"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".description-title"
  }, "All Events"), /* @__PURE__ */ jsx("div", {
    sel: ".description-body"
  }, "This board will automatically begin showing your data once events are sent to Mixpanel")), /* @__PURE__ */ jsx("div", {
    sel: ".event-item"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".icon"
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    attrs: { icon: `custom-event-beta` }
  })), /* @__PURE__ */ jsx("div", {
    sel: ".event-name"
  }, "All Events"))), this.params.mode === RHSPanelMode.Templates && /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", null, eventSelectStep()), /* @__PURE__ */ jsx(ButtonEl, {
    sel: ".submit-btn",
    attrs: {
      theme: ButtonTheme.Primary,
      disabled: this.helpers.shouldDisableFinishButton(),
      elref: `createTemplateButton`,
      icon: this.params.isLoading ? `spinner` : ``
    },
    on: {
      click: this.helpers.createTemplateBoard
    }
  }, "Save")))));
}
